import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  Divider,
  Skeleton,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  List as ListIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  Tune as TuneIcon,
  Storage as StorageIcon,
  Layers as LayersIcon,
  Lan as LanIcon,
} from '@mui/icons-material';
import { removePortalToken, getPortalUser } from '../../services/portalApi';
import { usePortalSession } from '../../hooks/usePortalSession';
import { getCapabilities } from '../../services/securityhubApi';
import { ENGINES } from '../../pages/portal/securityhub/engines/registry';
import { BASE_DOMAIN } from '../../utils/constants';

// Matches the Admin console sidebar (components/common/Sidebar.js) so the two
// consoles don't visually shift when a user moves between them.
const drawerWidth = 280;
const collapsedWidth = 72;

// Same entrance as the Admin sidebar: nav rows cascade in from the left.
const sidebarStagger = {
  initial: {},
  animate: { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
};
const sidebarItem = {
  initial: { opacity: 0, x: -8 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } },
};

// Helper function to derive display name from email
const getDisplayNameFromEmail = (email) => {
  if (!email) return 'User';
  const localPart = email.split('@')[0];
  return localPart
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const PortalSidebar = ({ user, mobileOpen = false, onMobileToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Which engine dropdowns are open, keyed by item.key.
  const [openGroups, setOpenGroups] = useState({});
  const { hasService } = usePortalSession();
  // Map of engineId → enabled. `null` means the fetch has not yet succeeded.
  // `capsLoading` gates the skeleton vs. real-item render to prevent a flash
  // where every engine appears enabled while the request is in-flight.
  const [engineEnabled, setEngineEnabled] = useState(null);
  // Map of engineId → available, derived from the SAME /capabilities response as
  // engineEnabled (no second request). Only engines implementing the optional
  // backend EngineAvailability interface can ever report false; for everyone
  // else the backend hard-codes true.
  const [engineAvailable, setEngineAvailable] = useState(null);
  const [capsLoading, setCapsLoading] = useState(true);
  // The skeleton (capsLoading) shows only until the FIRST settled fetch; later
  // refreshes (route change + poll) update engineEnabled silently so the sidebar
  // never re-flashes "Enable {Engine}" for an engine that is already enabled.
  const everLoadedCaps = useRef(false);

  useEffect(() => {
    if (!hasService('securityhub')) {
      // No fetch needed — clear loading immediately so the sidebar renders.
      setCapsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      if (!everLoadedCaps.current) setCapsLoading(true);
      getCapabilities()
        .then((res) => {
          if (cancelled) return;
          const rows = res?.data || [];
          const map = {};
          const availMap = {};
          rows.forEach((row) => {
            map[row.engine] = !!row.enabled;
            // Store the raw value, NOT a boolean coercion: the sidebar branches
            // on `=== false`, so an absent field must stay undefined (meaning
            // "available") rather than collapsing to false and hiding the row.
            availMap[row.engine] = row.available;
          });
          setEngineEnabled(map);
          setEngineAvailable(availMap);
        })
        .catch(() => {
          // On failure keep whatever engineEnabled was before — do NOT reset to
          // null (which previously caused every engine to flash as enabled).
        })
        .finally(() => {
          // Resolve the initial-loading skeleton unconditionally: once ANY fetch
          // settles we are past first load, even if this effect was superseded by
          // a rapid route change (the .then above still guards stale engineEnabled
          // writes). Guarding this on !cancelled could leave the skeleton stuck.
          everLoadedCaps.current = true;
          setCapsLoading(false);
        });
    };
    // Fetch on mount and whenever the route changes, plus a slow poll: enabling
    // or disabling an engine swaps its view in place without a route change, so a
    // one-time fetch would leave the sidebar stale ("Enable GuardDuty" after the
    // engine is already enabled) until a full page reload.
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [hasService, location.pathname]);

  const handleLogout = () => {
    const portalUser = getPortalUser();
    const realm = portalUser?.realm;

    removePortalToken();

    if (realm) {
      const baseDomain = BASE_DOMAIN.replace(/:\d+$/, '');
      const hostname = window.location.hostname;

      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        window.location.href = `http://apps.localhost:3000`;
      } else {
        window.location.href = `https://${realm}.${baseDomain}`;
      }
    } else {
      const baseDomain = BASE_DOMAIN.replace(/:\d+$/, '');
      window.location.href = `https://apps.${baseDomain}`;
    }
  };

  const isActive = (path) => {
    // "Trails" list path — also active when viewing a specific trail detail/edit.
    if (path === '/cloudtrail') {
      return (
        location.pathname === '/cloudtrail' ||
        (location.pathname.startsWith('/cloudtrail/trails/') && location.pathname !== '/cloudtrail/trails/create')
      );
    }
    // Security Hub Overview — exact match only (Findings/Configuration and the
    // CSPM engine routes have their own dedicated sidebar entries below).
    if (path === '/securityhub') {
      return location.pathname === '/securityhub';
    }
    // CSPM compliance entry — also active on the setup wizard / detail subpaths
    // that live directly under it with no dedicated sidebar entry.
    if (path === '/securityhub/cspm') {
      return location.pathname === '/securityhub/cspm';
    }
    // Security standards — active across the standards/detail/control drill-down subtree.
    if (path === '/securityhub/cspm/standards') {
      return location.pathname.startsWith('/securityhub/cspm/standards');
    }
    // IPAM pools — active across the pool-detail drill-down. Without this,
    // /securityhub/ipam/pools/ipam-pool-… highlights nothing and the sidebar
    // tells the operator they are nowhere.
    if (path === '/securityhub/ipam/pools') {
      return location.pathname.startsWith('/securityhub/ipam/pools');
    }
    // "StackSets" list path — also active when viewing a specific StackSet
    // detail/edit (create has its own dedicated sidebar entry).
    if (path === '/stacksets') {
      return (
        location.pathname === '/stacksets' ||
        (location.pathname.startsWith('/stacksets/') && location.pathname !== '/stacksets/create')
      );
    }
    return location.pathname === path;
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile && onMobileToggle) {
      onMobileToggle();
    }
  };

  // Opening a dropdown while the rail is collapsed has nowhere to render, so
  // expand the sidebar first — same behaviour as the Admin console.
  const handleGroupToggle = (key) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setOpenGroups((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Security Hub umbrella IA (mirrors the AWS Security Hub console). Built from
  // the engine registry so future detection engines appear automatically.
  // Security Hub IA: a single "Security Hub" group holding Overview plus one
  // collapsible row per engine. An engine's pages live inside its dropdown
  // (the same pattern the Admin console uses for Logs / Preferences) so the
  // sidebar stays short no matter how many engines are enabled.
  const buildSecurityHubItems = () => {
    const items = [
      { name: 'Overview', icon: <DashboardIcon />, path: '/securityhub' },
    ];
    ENGINES.forEach((engine) => {
      if (capsLoading) {
        // Emit a skeleton placeholder while capabilities are in-flight so we
        // never flash "Enable {Engine}" for an engine that is actually enabled.
        items.push({ skeleton: true, engineId: engine.id });
        return;
      }
      // DD4: an engine the deployment cannot offer gets NO row at all — not an
      // "Enable" row that leads to a page saying it cannot be enabled. The
      // explanation lives on the EnableCapabilities tile, which is where a user
      // goes to compare capabilities; the sidebar is navigation, and a nav item
      // that cannot lead anywhere is noise.
      //
      // `=== false`, not `!available`: the field is absent on a response from a
      // backend that predates it, and `!undefined` would hide every engine.
      if (engineAvailable?.[engine.id] === false) return;
      // After load: null engineEnabled (fetch failed with no prior value) →
      // treat every engine as disabled rather than enabled.
      const enabled = engineEnabled !== null && !!engineEnabled[engine.id];
      const EngineIcon = engine.Icon;
      if (enabled) {
        items.push({
          key: `engine-${engine.id}`,
          name: engine.label,
          icon: <EngineIcon />,
          // Every page of the engine, including its landing page (nav[0]),
          // lives in the dropdown — the parent row only opens and closes it.
          children: (engine.nav || []).map((sub) => ({ name: sub.label, path: sub.to })),
        });
      } else {
        items.push({
          name: `Enable ${engine.label}`,
          icon: <AddIcon />,
          path: engine.enableRoute,
          disabled: true,
        });
      }
    });
    items.push({
      key: 'aws-config',
      name: 'AWS Config',
      icon: <StorageIcon />,
      children: [
        { name: 'Recorders', path: '/securityhub/config' },
        { name: 'Config rules', path: '/securityhub/config-rules' },
      ],
    });
    // VPC IPAM. Rendered whether or not IPAM is enabled, exactly as AWS Config's
    // group is, because /capabilities iterates registry.All() and IPAM is
    // deliberately NOT an engine (D-2) — so the response carries no `ipam` row to
    // gate on. That is not a lie: an un-enabled tenant clicking Dashboard lands
    // on IpamEntry, resolves config === null and gets the setup wizard, which is
    // the correct destination.
    items.push({
      key: 'ipam',
      name: 'VPC IPAM',
      icon: <LanIcon />,
      children: [
        { name: 'Dashboard', path: '/securityhub/ipam' },
        { name: 'Address space', path: '/securityhub/ipam/address-space' },
        { name: 'Pools', path: '/securityhub/ipam/pools' },
        { name: 'Allocations', path: '/securityhub/ipam/allocations' },
        { name: 'Public IPs', path: '/securityhub/ipam/public-ips' },
        { name: 'Accounts', path: '/securityhub/ipam/accounts' },
        { name: 'Settings', path: '/securityhub/ipam/settings' },
      ],
    });
    items.push({ name: 'Configuration', icon: <TuneIcon />, path: '/securityhub/configuration' });
    return items;
  };

  const serviceGroups = [
    hasService('cloudtrail') && {
      name: 'CloudTrail',
      items: [
        { name: 'Trails', icon: <ListIcon />, path: '/cloudtrail' },
        { name: 'Create Trail', icon: <AddIcon />, path: '/cloudtrail/trails/create' },
      ],
    },
    hasService('securityhub') && {
      name: 'Security Hub',
      items: buildSecurityHubItems(),
    },
    hasService('stacksets') && {
      name: 'CF StackSets',
      items: [
        // Creating a StackSet starts from the "Create StackSet" button on the
        // list page, so the wizard needs no sidebar entry of its own.
        { name: 'StackSets', icon: <LayersIcon />, path: '/stacksets' },
      ],
    },
  ].filter(Boolean);

  const effectiveCollapsed = isMobile ? false : isCollapsed;

  const drawerContent = (
    <>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: effectiveCollapsed ? 'center' : 'space-between',
          padding: effectiveCollapsed ? '16px 8px' : '12px 20px',
          // The Admin header box is a 72px single-line lockup; the portal adds
          // an "Organization Services" caption under the logo, so this box needs
          // the extra room or the caption clips on every page.
          minHeight: 72,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        {!effectiveCollapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <img
              src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
              alt="CloudKeeper"
              style={{
                height: '28px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              Organization Services
            </Typography>
          </Box>
        )}
        {isMobile ? (
          <IconButton
            onClick={onMobileToggle}
            sx={{
              color: '#253E66',
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#F0F4F8',
                borderColor: '#D9DBDC',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <Tooltip title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                color: '#253E66',
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#F0F4F8',
                  borderColor: '#D9DBDC',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Navigation List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 2,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#F9FBFD', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb': { background: '#D9DBDC', borderRadius: '2px' },
        }}
      >
        {/* One stagger container across ALL groups — a per-group CSS delay made
            every group start at zero, so the groups popped in simultaneously
            instead of cascading the way the Admin sidebar does. */}
        <List component="div" sx={{ p: 0 }}>
        <motion.div variants={sidebarStagger} initial="initial" animate="animate">
          {serviceGroups.map((group, gIdx) => (
            <Box key={group.name} sx={{ mb: gIdx < serviceGroups.length - 1 ? 1.5 : 0 }}>
              {!effectiveCollapsed && (
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: gIdx === 0 ? 0 : 1,
                    pb: 0.5,
                    color: '#9AA3B2',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    letterSpacing: '0.08em',
                  }}
                >
                  {group.name}
                </Typography>
              )}
              {group.items.map((item) => {
                // Non-clickable subheader (e.g. "Detection engines"). Hidden
                // when the sidebar is collapsed since there's no room for text.
                if (item.subheader) {
                  if (effectiveCollapsed) return null;
                  return (
                    <Typography
                      key={`sub-${item.subheader}`}
                      variant="overline"
                      sx={{
                        display: 'block',
                        px: 2,
                        pt: 1,
                        pb: 0.5,
                        color: '#B0B7C3',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.6rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {item.subheader}
                    </Typography>
                  );
                }
                // Skeleton placeholder emitted while capabilities are loading.
                if (item.skeleton) {
                  if (effectiveCollapsed) return null;
                  return (
                    <ListItem component="div" key={`skeleton-${item.engineId}`} disablePadding sx={{ mb: 0.5 }}>
                      <Box sx={{ px: 2, py: 0.75, width: '100%' }}>
                        <Skeleton variant="text" width="70%" height={28} sx={{ borderRadius: '4px' }} />
                      </Box>
                    </ListItem>
                  );
                }
                // Indented engine sub-items have no icon — they'd render as
                // blank rows when collapsed, so hide them there. The engine
                // header (with icon + tooltip) still represents the engine.
                if (item.indent && effectiveCollapsed) return null;

                // Collapsible group (an engine, or AWS Config): the row opens
                // and closes a dropdown holding that capability's pages.
                if (item.children) {
                  const groupActive = item.children.some((c) => isActive(c.path));
                  // Auto-open the group the user is currently inside, so the
                  // active page is never hidden behind a closed dropdown.
                  const open = openGroups[item.key] ?? groupActive;
                  return (
                    <motion.div key={item.key} variants={sidebarItem}>
                      <ListItem component="div" disablePadding sx={{ mb: 0.5 }}>
                        <Tooltip title={effectiveCollapsed ? item.name : ''} placement="right">
                          <ListItemButton
                            onClick={() => handleGroupToggle(item.key)}
                            selected={groupActive}
                            sx={{
                              borderRadius: '6px',
                              color: groupActive ? '#4698D3' : '#666666',
                              backgroundColor: groupActive ? 'rgba(70, 152, 211, 0.08)' : 'transparent',
                              border: groupActive
                                ? '1px solid rgba(70, 152, 211, 0.2)'
                                : '1px solid transparent',
                              '&:hover': {
                                backgroundColor: groupActive
                                  ? 'rgba(70, 152, 211, 0.12)'
                                  : '#F9FBFD',
                                color: groupActive ? '#4698D3' : '#253E66',
                              },
                              justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                              pl: effectiveCollapsed ? 1.5 : 2,
                              pr: effectiveCollapsed ? 1.5 : 2,
                              py: 1.25,
                              minHeight: 44,
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: 'inherit',
                                minWidth: effectiveCollapsed ? 'auto' : 36,
                                justifyContent: 'center',
                                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            {!effectiveCollapsed && (
                              <>
                                <ListItemText
                                  primary={item.name}
                                  primaryTypographyProps={{
                                    fontFamily: 'Metropolis, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                  }}
                                />
                                {open ? (
                                  <ExpandLessIcon sx={{ fontSize: '1.1rem' }} />
                                ) : (
                                  <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
                                )}
                              </>
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>

                      <Collapse in={open && !effectiveCollapsed} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 2 }}>
                          {item.children.map((child) => {
                            const childActive = isActive(child.path);
                            return (
                              <ListItem
                                component="div"
                                key={child.path}
                                disablePadding
                                sx={{ mb: 0.25 }}
                              >
                                <ListItemButton
                                  onClick={() => handleNavigate(child.path)}
                                  selected={childActive}
                                  sx={{
                                    borderRadius: '6px',
                                    color: childActive ? '#4698D3' : '#666666',
                                    backgroundColor: childActive
                                      ? 'rgba(70, 152, 211, 0.08)'
                                      : 'transparent',
                                    border: childActive
                                      ? '1px solid rgba(70, 152, 211, 0.15)'
                                      : '1px solid transparent',
                                    '&:hover': {
                                      backgroundColor: childActive
                                        ? 'rgba(70, 152, 211, 0.12)'
                                        : '#F9FBFD',
                                      color: childActive ? '#4698D3' : '#253E66',
                                    },
                                    py: 0.75,
                                    px: 2,
                                    minHeight: 36,
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <ListItemText
                                    primary={child.name}
                                    primaryTypographyProps={{
                                      fontFamily: 'Metropolis, sans-serif',
                                      fontWeight: childActive ? 600 : 500,
                                      fontSize: '0.82rem',
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    </motion.div>
                  );
                }

                const active = isActive(item.path);
                return (
                  <motion.div key={item.key || item.path} variants={sidebarItem}>
                  <ListItem component="div" disablePadding sx={{ mb: 0.5 }}>
                    <Tooltip title={effectiveCollapsed ? item.name : ''} placement="right">
                      <ListItemButton
                        onClick={() => handleNavigate(item.path)}
                        selected={active}
                        sx={{
                          borderRadius: '6px',
                          color: item.disabled ? '#9AA3B2' : (active ? '#4698D3' : '#666666'),
                          backgroundColor: active ? 'rgba(70, 152, 211, 0.08)' : 'transparent',
                          border: active ? '1px solid rgba(70, 152, 211, 0.2)' : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: active
                              ? 'rgba(70, 152, 211, 0.12)'
                              : '#F9FBFD',
                            color: item.disabled ? '#666666' : (active ? '#4698D3' : '#253E66'),
                          },
                          justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                          // Indent engine sub-items (Compliance/Enrollment/Settings)
                          // under their engine header when expanded.
                          pl: effectiveCollapsed ? 1.5 : (item.indent ? 4.5 : 2),
                          pr: effectiveCollapsed ? 1.5 : 2,
                          py: item.indent ? 0.75 : 1.25,
                          minHeight: item.indent ? 36 : 44,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {item.icon && (
                          <ListItemIcon
                            sx={{
                              color: 'inherit',
                              minWidth: effectiveCollapsed ? 'auto' : 36,
                              justifyContent: 'center',
                              '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                        )}
                        {!effectiveCollapsed && (
                          <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: active ? 600 : (item.engineHeader ? 600 : 500),
                              fontSize: item.indent ? '0.82rem' : '0.875rem',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                  </motion.div>
                );
              })}
            </Box>
          ))}

          {/* Mobile Logout Button */}
          {isMobile && (
            <>
              <Box sx={{ py: 1 }}>
                <Divider sx={{ borderColor: '#E5E5E5' }} />
              </Box>
              <ListItem component="div" disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '6px',
                    color: '#E74C3C',
                    '&:hover': {
                      backgroundColor: 'rgba(231, 76, 60, 0.08)',
                      color: '#C0392B',
                    },
                    px: 2,
                    py: 1.25,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: 36,
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </motion.div>
        </List>
      </Box>

      {/* Bottom section - User Info */}
      <Box sx={{
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 1,
      }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#F9FBFD',
            borderTop: '1px solid #E5E5E5',
            padding: effectiveCollapsed ? '12px 8px' : '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: effectiveCollapsed ? 'center' : 'space-between',
            borderRadius: 0,
            minHeight: 68,
            maxHeight: 68,
          }}
        >
          {!effectiveCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: '#4698D3',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  flexShrink: 0,
                }}
              >
                {getDisplayNameFromEmail(user?.email).charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getDisplayNameFromEmail(user?.email)}
                </Typography>
                {/* No role line: Organization Services has exactly one kind of
                    user, so naming it told the reader nothing. */}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Logout" placement="top">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#E74C3C',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    borderColor: '#E74C3C',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LogoutIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid #E5E5E5',
            borderRadius: 0,
            overflowX: 'hidden',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={true}
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E5E5',
          borderRadius: 0,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          overflowY: 'hidden',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default PortalSidebar;
